import type { EventBootstrapResponse } from '@irace/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { submitCheckin, submitCheckout } from '../api/client.ts';
import type { CheckoutRecord } from '../db/checkout-record.ts';
import {
  IndexedDbCheckoutStore,
  IndexedDbOutbox,
  type CheckoutStore,
  type OutboxStore,
} from '../db/indexeddb.ts';
import type { OutboxRecord } from '../db/outbox-record.ts';
import { CheckoutService, type CheckoutSendResult } from '../sync/checkout.ts';
import { CheckoutRetry } from '../sync/checkout-retry.ts';
import { LocalSaveError, SubmissionService, type SendResult } from '../sync/outbox.ts';
import { RetryEngine } from '../sync/retry.ts';
import { resolveSiteKey, TurnstileClient } from '../turnstile/client.ts';
import type { CheckinSubmission } from './CheckinForm.tsx';
import type { CheckoutState } from './CheckoutPanel.tsx';
import { enabledCheckout, resumableCheckin } from './checkoutResume.ts';

/**
 * G-002 / G-003 / H-001 / H-003 — the submit state machine of §11, wired to
 * the outbox, Turnstile and the retry engine.
 *
 * The page component stays declarative: it renders whichever screen the
 * current state names, and every transition here is one the spec draws.
 */

export type FlowState =
  | { kind: 'form' }
  /** M04 (§14). `verifying` is Turnstile, `sending` is the POST — §13 forbids conflating them. */
  | { kind: 'submitting'; phase: 'saving' | 'verifying' | 'sending' }
  /** M05 (§15) — saved locally, no 202 yet. Amber, never green. */
  | { kind: 'pending'; record: OutboxRecord }
  /** M06 (§16) — the token failed; the submission did not. */
  | { kind: 'verify_retry'; record: OutboxRecord }
  /** M07 (§17) — reached only after HTTP 202. */
  | { kind: 'accepted'; record: OutboxRecord }
  /** A server rejection the participant has to act on (bad location, too long). */
  | { kind: 'rejected'; message: string }
  /** §12 — the local write failed, so nothing was sent. Recoverable by retrying. */
  | { kind: 'save_failed' };

/** §17 — accepted records are kept briefly so a reload can still confirm, then cleaned. */
const ACCEPTED_RETENTION_MS = 6 * 60 * 60 * 1000;

function stateFor(result: SendResult): FlowState {
  switch (result.kind) {
    case 'accepted':
      return { kind: 'accepted', record: result.record };
    case 'verify_retry':
      return { kind: 'verify_retry', record: result.record };
    case 'rejected':
      return { kind: 'rejected', message: result.message };
    case 'pending':
      return { kind: 'pending', record: result.record };
  }
}

/**
 * G-014 — where a check-out send left the participant.
 *
 * `verify_retry` and `rejected` both land on `failed`, and that is the whole
 * point: the panel's only affordance is "try again", and both of those recover
 * that way — a fresh challenge for the first, and for the second the only
 * reachable terminal codes are ones the participant can do nothing else about.
 */
function checkoutStateFor(result: CheckoutSendResult): CheckoutState {
  switch (result.kind) {
    case 'accepted':
      return 'done';
    case 'pending':
      return 'pending';
    case 'verify_retry':
    case 'rejected':
      return 'failed';
  }
}

export interface CheckinFlow {
  state: FlowState;
  /** §19 — how many records are still waiting, for the recovery banner. */
  pendingCount: number;
  /** True once a pending record was found at startup and not yet resolved (§18). */
  recovered: boolean;
  /** A manual retry is in flight; the screen stays put and the button waits. */
  retrying: boolean;
  turnstileContainerRef: React.RefObject<HTMLDivElement | null>;
  submit: (submission: CheckinSubmission) => void;
  retryNow: () => void;
  /** Back to a blank form for a deliberate second check-in (new submission_id). */
  reset: () => void;
  /** G-014 — where the current check-out stands, or `idle` if none was started. */
  checkoutState: CheckoutState;
  /** Save and send a departure. A no-op once one has been accepted. */
  checkout: () => void;
  /**
   * An accepted check-in from an earlier visit that still has no check-out, so
   * a reload opens on the confirmation instead of an empty form — with the
   * check-out panel when the event has one, without it otherwise.
   *
   * A candidate, not a verdict: pass it through `resumeTakesOver` before
   * letting it replace anything.
   */
  resumable: OutboxRecord | null;
  /** True once the participant has touched the form in any way. One-way. */
  formDirty: boolean;
  /** Called by the form on the first interaction of any kind. */
  markFormTouched: () => void;
}

export function useCheckinFlow(bootstrap: EventBootstrapResponse): CheckinFlow {
  const [state, setState] = useState<FlowState>({ kind: 'form' });
  const [pendingCount, setPendingCount] = useState(0);
  const [recovered, setRecovered] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [checkoutState, setCheckoutStateRaw] = useState<CheckoutState>('idle');
  const [resumable, setResumable] = useState<OutboxRecord | null>(null);
  /** One-way for the life of the visit; see `resumeTakesOver`. */
  const [formDirty, setFormDirty] = useState(false);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  /** Mirrors `checkoutState` so a double tap cannot slip past a stale render. */
  const checkoutStateRef = useRef<CheckoutState>('idle');
  /** The departure this screen owns; null until the participant taps. */
  const checkoutRecordRef = useRef<CheckoutRecord | null>(null);

  const { event, form } = bootstrap;
  /**
   * An event without check-out gets exactly the screens it had before this
   * feature existed — no second IndexedDB store opened, no second retry loop
   * running. `enabledCheckout` is shared with the screens so the engine and
   * the button can never gate on different conditions.
   */
  const checkoutEnabled = enabledCheckout(bootstrap.checkout) !== null;

  const setCheckoutState = useCallback((next: CheckoutState) => {
    checkoutStateRef.current = next;
    setCheckoutStateRaw(next);
  }, []);

  const engine = useMemo(() => {
    let outbox: OutboxStore;
    try {
      outbox = new IndexedDbOutbox();
    } catch {
      // Private mode or a locked-down browser. Submitting is refused rather
      // than sent unsaved: an unsaved send that fails is data the participant
      // cannot recover (§12).
      return null;
    }

    const siteKey = resolveSiteKey(
      import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined,
      import.meta.env.PROD,
    );
    if (!siteKey) {
      throw new Error('VITE_TURNSTILE_SITE_KEY is required for a production build');
    }

    const turnstile = new TurnstileClient({
      siteKey,
      container: () => turnstileContainerRef.current,
    });

    const submission = new SubmissionService({ outbox, turnstile, submit: submitCheckin });

    const retry = new RetryEngine({
      outbox,
      eventId: event.id,
      send: (record) => submission.send(record),
      onQueueChange: (pending) => {
        setPendingCount(pending.length);
      },
      onResult: (result) => {
        setState(stateFor(result));
      },
    });

    /**
     * The check-out half, built from the same Turnstile client on purpose.
     *
     * One widget, therefore one action. `POST /api/v1/checkouts` verifies
     * against the same configured `TURNSTILE_ACTION` as check-in, so a second
     * client rendered with a check-out-flavoured action would mint tokens
     * Siteverify reports as `action-mismatch` and turn every departure into a
     * 403 — unrecoverable at the venue and invisible until event day. There is
     * no `action` option anywhere below, and that is deliberate.
     */
    const checkout = checkoutEnabled
      ? (() => {
          const store: CheckoutStore = new IndexedDbCheckoutStore();
          const service = new CheckoutService({ store, turnstile, submit: submitCheckout });

          const owned = (record: CheckoutRecord) =>
            record.submissionId === checkoutRecordRef.current?.submissionId;

          const retry = new CheckoutRetry({
            store,
            eventId: event.id,
            send: (record) => service.send(record),
            onResult: (result) => {
              // Only the departure this screen is showing. A record recovered
              // from an earlier visit has no panel on screen, and reporting it
              // would relabel a check-out the participant is not looking at.
              if (owned(result.record)) setCheckoutState(checkoutStateFor(result));
            },
            onError: () => {
              /*
               * A pass that could not even read the queue — a blocked
               * IndexedDB upgrade, in practice. The loop self-heals for about
               * a minute and then goes quiet, so if a departure is waiting on
               * that queue the participant has to be given the manual way
               * back in. `failed` is that: it is the one state whose copy asks
               * them to try again, and the retry re-sends the record already
               * on disk without touching its timestamp.
               *
               * Only from `pending`. From `idle` there is nothing waiting and
               * nothing to warn about, and a device that shows "chưa gửi
               * được" before the button was ever pressed teaches people to
               * ignore the message that matters.
               */
              if (checkoutStateRef.current === 'pending') setCheckoutState('failed');
            },
          });

          return { store, service, retry };
        })()
      : null;

    return { outbox, submission, retry, checkout };
  }, [checkoutEnabled, event.id, setCheckoutState]);

  useEffect(() => {
    if (!engine) return;

    // §18 — surface anything left over from a previous visit before the retry
    // engine quietly starts working on it.
    void engine.outbox.listPending(event.id).then((pending) => {
      setPendingCount(pending.length);
      const [first] = pending;
      if (first) {
        setRecovered(true);
        setState({ kind: 'pending', record: first });
      }
    });

    engine.retry.start();

    const checkout = engine.checkout;

    // Find out whether this device is returning to an event it already checked
    // into. This resolves *after* the first render, and can resolve long after
    // it when a stale tab is blocking the database, so the candidate found here
    // is an offer, not a decision: `resumeTakesOver` decides at render time
    // whether it may still take the screen.
    //
    // Runs for every event, not only the ones with check-out (G-014). An
    // accepted check-in used to be reachable on exactly one render and then
    // gone: a reload dropped the participant back on an empty form, which
    // reads as "it did not go through" and invites a second submission of the
    // same person under a fresh `submission_id` — one the UNIQUE constraint on
    // that column cannot catch. The store already keeps the record six hours
    // (§17) for precisely this; nothing was reading it.
    void (async () => {
      try {
        // Before the read, not beside it: what this decides is whether a
        // confirmation takes the screen, and a record older than the retention
        // window must be gone rather than merely on its way out. Racing the
        // prune would show yesterday's check-in to whoever opens the page
        // today.
        await engine.outbox.pruneAccepted(ACCEPTED_RETENTION_MS);

        const [records, checkouts] = await Promise.all([
          engine.outbox.listAll(event.id),
          checkout ? checkout.store.listAll(event.id) : Promise.resolve<CheckoutRecord[]>([]),
        ]);
        setResumable(resumableCheckin(records, checkouts, event.id));
      } catch {
        // The database would not open — a stale tab holding the previous
        // version is the realistic cause. Falling back to the form is safe:
        // it is the screen this participant would have seen anyway, and it
        // is the screen that can still record a check-in.
      }
    })();

    if (checkout) checkout.retry.start();

    return () => {
      engine.retry.stop();
      checkout?.retry.stop();
    };
  }, [engine, event.id]);

  const submit = useCallback(
    (submission: CheckinSubmission) => {
      if (!engine) {
        setState({ kind: 'save_failed' });
        return;
      }

      // A deliberate new submission takes over the screen from any recovered
      // record: this one is what the participant is standing there waiting for.
      setRecovered(false);
      // A new arrival is a new departure to record later, so the panel must
      // not open on the previous one's outcome.
      setResumable(null);
      checkoutRecordRef.current = null;
      setCheckoutState('idle');
      setState({ kind: 'submitting', phase: 'saving' });

      void (async () => {
        let record: OutboxRecord;
        try {
          record = await engine.submission.save({
            eventId: event.id,
            eventConfigVersion: event.config_version,
            locationId: submission.locationId,
            locationName: submission.locationName,
            fields: form.fields,
            values: submission.values,
          });
        } catch (error) {
          setState({ kind: 'save_failed' });
          if (!(error instanceof LocalSaveError)) throw error;
          return;
        }

        setPendingCount((count) => count + 1);
        setState({ kind: 'submitting', phase: 'verifying' });
        setState(stateFor(await engine.submission.send(record)));
      })();
    },
    [engine, event.config_version, event.id, form.fields, setCheckoutState],
  );

  const retryNow = useCallback(() => {
    if (!engine) return;
    // The pending screen stays on screen while this runs: the engine reports
    // the outcome through onResult, and flipping to a "sending" screen first
    // would hide the challenge the participant may need to solve.
    setRetrying(true);
    void engine.retry.retryNow().finally(() => {
      setRetrying(false);
    });
  }, [engine]);

  /**
   * G-014 — record a departure, save first (AGENTS.md invariant 2).
   *
   * Which check-in it closes is not a choice the button makes: it is the one
   * currently on screen — this session's accepted record, or the one the
   * resume screen was opened on. A check-out never mints its own id
   * (invariant 4).
   */
  const checkout = useCallback(() => {
    const target = state.kind === 'accepted' ? state.record : resumable;
    const checkoutEngine = engine?.checkout;

    if (!checkoutEngine || !target) {
      // No store means nothing can be saved, and §12 says a send whose record
      // is not on disk is worse than no send at all. `failed` rather than a
      // dead button: the copy asks them to try again, and a store blocked by a
      // stale tab starts working the moment that tab closes.
      setCheckoutState('failed');
      return;
    }

    /*
     * The deliberate answer to "may the UI call save() again after a check-out
     * was accepted": no.
     *
     * `save()` re-puts the record as pending with a zero attempt count even
     * over an accepted one, so a second tap would put work the server has
     * already taken back into the retry queue, burn another Turnstile
     * challenge, and — the part that actually loses information — stamp
     * `clientCheckedOutAt` with the moment of the second tap. The participant
     * left once. The server is idempotent on submission_id so no row is
     * corrupted, but the device's own record of when they left would drift
     * later every time someone fidgeted with the button.
     *
     * So it is guarded twice: the panel renders no button in `done`, and this
     * refuses anyway, because a re-render is not a lock and a double tap
     * arrives before one.
     */
    if (checkoutStateRef.current === 'sending' || checkoutStateRef.current === 'done') return;

    setCheckoutState('sending');

    void (async () => {
      let record = checkoutRecordRef.current;

      if (!record) {
        try {
          record = await checkoutEngine.service.save({
            submissionId: target.submissionId,
            eventId: target.eventId,
          });
          checkoutRecordRef.current = record;
        } catch (error) {
          setCheckoutState('failed');
          if (!(error instanceof LocalSaveError)) throw error;
          return;
        }
      }

      // A retry re-sends the record already on disk. Going back through
      // `save()` would work, but it would move the departure time to the
      // moment of the retry rather than the moment they left.
      setCheckoutState(checkoutStateFor(await checkoutEngine.service.send(record)));
    })();
  }, [engine, resumable, setCheckoutState, state]);

  /**
   * The form telling us it is no longer blank.
   *
   * Cheap on purpose: it flips one boolean once, and the form calls it on
   * every interaction rather than trying to work out which ones "count" —
   * an opened location picker is as much a thing you must not yank away as a
   * half-typed name.
   */
  const markFormTouched = useCallback(() => {
    setFormDirty(true);
  }, []);

  const reset = useCallback(() => {
    setRecovered(false);
    // Leaving the resume screen for the form is a deliberate second check-in;
    // any pending check-out stays on disk and stays the retry loop's problem.
    setResumable(null);
    checkoutRecordRef.current = null;
    setCheckoutState('idle');
    setState({ kind: 'form' });
  }, [setCheckoutState]);

  return {
    state,
    pendingCount,
    recovered,
    retrying,
    turnstileContainerRef,
    submit,
    retryNow,
    reset,
    checkoutState,
    checkout,
    resumable,
    formDirty,
    markFormTouched,
  };
}
